import React, { createContext, useState, useCallback, useContext, useRef } from 'react';

export const UIContext = createContext(null);

// Default timeout for success alerts (in milliseconds)
const DEFAULT_ALERT_TIMEOUT = 5000;

export const UIProvider = ({ children }) => {
  // Single loading state - tracks both initialization and user operations
  const [loading, setLoading] = useState({ show: true, message: 'Loading...' });
  const [alert, setAlert] = useState({ show: false, type: 'info', message: '', dismissible: true });
  const [isInitialized, setIsInitialized] = useState(false);
  const alertTimeoutRef = useRef(null);

  const showLoading = useCallback((message = 'Loading...') => {
    setLoading({ show: true, message });
  }, []);

  const hideLoading = useCallback(() => {
    if (isInitialized) {
      setLoading({ show: false, message: '' });
    }
  }, [isInitialized]);

  // Called by AuthContext when initialization is complete
  const setReady = useCallback(() => {
    setIsInitialized(true);
    setLoading({ show: false, message: '' });
  }, []);

  const hideAlert = useCallback(() => {
    // Clear any pending timeout
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
      alertTimeoutRef.current = null;
    }
    setAlert(prev => ({ ...prev, show: false }));
  }, []);

  const showAlert = useCallback((message, type = 'info', dismissible = true, timeout = null) => {
    // Clear any existing timeout
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
      alertTimeoutRef.current = null;
    }

    setAlert({ show: true, type, message, dismissible });

    // Auto-hide after timeout if specified
    if (timeout && timeout > 0) {
      alertTimeoutRef.current = setTimeout(() => {
        setAlert(prev => ({ ...prev, show: false }));
        alertTimeoutRef.current = null;
      }, timeout);
    }
  }, []);

  const showSuccess = useCallback((message, dismissible = true, timeout = DEFAULT_ALERT_TIMEOUT) => {
    showAlert(message, 'success', dismissible, timeout);
  }, [showAlert]);

  const showError = useCallback((message, dismissible = true, timeout = null) => {
    showAlert(message, 'danger', dismissible, timeout);
  }, [showAlert]);

  const showWarning = useCallback((message, dismissible = true, timeout = null) => {
    showAlert(message, 'warning', dismissible, timeout);
  }, [showAlert]);

  return (
    <UIContext.Provider value={{
      loading,
      isInitialized,
      alert,
      showLoading,
      hideLoading,
      setReady,
      showAlert,
      showSuccess,
      showError,
      showWarning,
      hideAlert,
    }}>
      {children}
    </UIContext.Provider>
  );
};

/**
 * Custom hook for accessing UI context.
 * @returns {Object} UI context value
 */
export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};
